import { useMemo, useState } from 'react';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';
import {
   PrescriptionStatusForContinuouslyUsedMedicines,
   Visit,
   VisitDraftPrescriptionsQuantityQuery,
} from '@dreryk/egabinet-visit-frontend-api';

import { refetchCqrsQuery } from '@dreryk/command-query-react-common';
import { assertUnreachable } from '@dreryk/react-common';
import { CheckIcon, IconSize, IconWrapper } from '@dreryk/react-components';

import { VisitDiagnoses } from '~libs.shared.visit';
import { MedicinePackage } from '~libs.desktop.shared.medicine';

import { ContinuouslyUsedMedicinesPrescriptionStatus } from '../VisitContinuouslyUsedMedicineTable';

import { MedicineOutdatedIcon } from './MedicineOutdatedIcon/MedicineOutdatedIcon';
import { StyledContinuouslyUsedMedicineCheckContainer } from './rowElements/StyledContinuouslyUsedMedicineCheckContainer';
import { VisitContinuouslyUsedMedicineName } from './rowElements/VisitContinuouslyUsedMedicineName';
import { VisitContinuouslyUsedMedicineTableCheckbox } from './rowElements/VisitContinuouslyUsedMedicineTableCheckbox';
import {
   useContinuouslyUsedMedicinePrescriptionDraftCreateCommand,
   useContinuouslyUsedMedicinePrescriptionDraftRemoveCommand,
} from './VisitContinuouslyUsedMedicineTableRow.hooks';
import { StyledVisitContinuouslyUsedMedicineTableRow } from './VisitContinuouslyUsedMedicineTableRow.styled';
import { StyledVisitContinuouslyUsedMedicineTableRowMedicine } from './VisitContinuouslyUsedMedicineTableRowMedicine.styled';

type VisitContinuouslyUsedMedicineTableRowProps = {
   readonly prescriptionForContinuouslyUsedMedicinesStatus: PrescriptionStatusForContinuouslyUsedMedicines;
   patientContinuouslyUsedMedicineId: number;
   readonly medicineId: number;
   readonly visitId: Visit['id'];
   readonly isReadonly: boolean | undefined;
   readonly patientId: PatientSimple['id'];
   readonly medicineSimple: MedicineSimple;
   visitDiagnoses: VisitDiagnoses;
};

// eslint-disable-next-line max-lines-per-function
const VisitContinuouslyUsedMedicineTableRow = (props: VisitContinuouslyUsedMedicineTableRowProps): JSX.Element => {
   const [prescriptionStatus, setPrescriptionStatus] = useState(props.prescriptionForContinuouslyUsedMedicinesStatus);

   const onSuccess = (): void => {
      refetchCqrsQuery(VisitDraftPrescriptionsQuantityQuery);
      setPrescriptionStatus((prev) =>
         prev === ContinuouslyUsedMedicinesPrescriptionStatus.NO_PRESCRIPTION
            ? ContinuouslyUsedMedicinesPrescriptionStatus.DRAFT
            : ContinuouslyUsedMedicinesPrescriptionStatus.NO_PRESCRIPTION
      );
   };

   const {
      executeAsync: removeContinuouslyUsedMedicinePrescriptionDraft,
      loading: deletionLoading,
   } = useContinuouslyUsedMedicinePrescriptionDraftRemoveCommand(props.visitId, props.medicineId, onSuccess);

   const {
      executeAsync: createContinuouslyUsedMedicinePrescriptionDraft,
      loading: creationLoading,
   } = useContinuouslyUsedMedicinePrescriptionDraftCreateCommand(
      props.visitId,
      props.medicineId,
      props.patientId,
      onSuccess
   );

   const requestHandler = useMemo<VoidFunction | null>(() => {
      if (props.isReadonly) {
         return null;
      }

      switch (prescriptionStatus) {
         case 'NO_PRESCRIPTION':
            return createContinuouslyUsedMedicinePrescriptionDraft;
         case 'DRAFT':
            return removeContinuouslyUsedMedicinePrescriptionDraft;
         case 'ACCEPTED':
            return null;
         default:
            return assertUnreachable(prescriptionStatus);
      }
   }, [
      props.isReadonly,
      prescriptionStatus,
      createContinuouslyUsedMedicinePrescriptionDraft,
      removeContinuouslyUsedMedicinePrescriptionDraft,
   ]);

   return (
      <StyledVisitContinuouslyUsedMedicineTableRow justifyContent='space-between' gap={'6px'} alignItems={'center'}>
         <StyledVisitContinuouslyUsedMedicineTableRowMedicine>
            <StyledContinuouslyUsedMedicineCheckContainer>
               {ContinuouslyUsedMedicinesPrescriptionStatus.ACCEPTED ===
               props.prescriptionForContinuouslyUsedMedicinesStatus ? (
                  <IconWrapper size={IconSize.SMALL} testId={'prescriptionForContinuouslyUsedMedicines'}>
                     <CheckIcon />
                  </IconWrapper>
               ) : (
                  <VisitContinuouslyUsedMedicineTableCheckbox
                     medicineId={props.medicineId}
                     prescriptionStatus={prescriptionStatus}
                     isReadonly={props.isReadonly || !props.medicineSimple.valid}
                     handleChange={requestHandler}
                     requestLoading={creationLoading || deletionLoading}
                  />
               )}
            </StyledContinuouslyUsedMedicineCheckContainer>
            <div>
               <VisitContinuouslyUsedMedicineName
                  medicine={props.medicineSimple}
                  requestHandler={requestHandler}
                  prescriptionStatus={prescriptionStatus}
               />
               <MedicinePackage
                  packageContent={props.medicineSimple.packageContent}
                  form={props.medicineSimple.form}
                  dose={props.medicineSimple.dose}
                  valid={props.medicineSimple.valid}
               />
            </div>
         </StyledVisitContinuouslyUsedMedicineTableRowMedicine>
         {props.medicineSimple.valid ? null : (
            <MedicineOutdatedIcon
               withdrawnMedicine={props.medicineSimple}
               medicineId={props.patientContinuouslyUsedMedicineId}
               patientId={props.patientId}
               visitDiagnoses={props.visitDiagnoses}
            />
         )}
      </StyledVisitContinuouslyUsedMedicineTableRow>
   );
};

export { VisitContinuouslyUsedMedicineTableRow };
